/**
 * Role-based access control configuration for the app frontend.
 *
 * This module centralizes all role-based access logic for the app frontend,
 * which serves three personas: self-care, assist, and admin.
 */

import type { TenantMembership } from '../types';

export type PersonaRoute = 'self-care' | 'assist' | 'admin';

/**
 * Roles that can access the app frontend at all.
 * Users with roles not in this list should be shown a 403 page.
 */
const APP_ALLOWED_ROLES = [
  'platform-admin',
  'tenant-owner',
  'tenant-admin',
  'partner-admin',
  'owner',
  'user',
  'member',
  'caregiver',
  'care-coordinator',
];

/**
 * Role requirements for each persona route.
 * Higher-privilege roles (admin, assist) have stricter requirements.
 */
const PERSONA_ALLOWED_ROLES: Record<PersonaRoute, string[]> = {
  'self-care': ['platform-admin', 'tenant-owner', 'tenant-admin', 'partner-admin', 'owner', 'user', 'member', 'caregiver', 'care-coordinator'],
  assist: ['platform-admin', 'tenant-owner', 'tenant-admin', 'partner-admin', 'owner', 'caregiver', 'care-coordinator'],
  admin: ['platform-admin', 'tenant-owner', 'tenant-admin', 'partner-admin', 'owner'],
};

/**
 * Check if user has any role that grants app frontend access.
 *
 * @param roles - User's roles from current tenant membership
 * @returns true if user can access the app frontend
 */
export function canAccessAppFrontend(roles: string[]): boolean {
  const normalizedRoles = roles.map((r) => r.toLowerCase());
  return normalizedRoles.some((role) =>
    APP_ALLOWED_ROLES.map((r) => r.toLowerCase()).includes(role)
  );
}

/**
 * Check if user can access a specific persona route.
 *
 * @param roles - User's roles from current tenant membership
 * @param persona - The persona route to check access for
 * @returns true if user can access the specified persona
 */
export function canAccessPersona(roles: string[], persona: PersonaRoute): boolean {
  const allowedRoles = PERSONA_ALLOWED_ROLES[persona];
  const normalizedRoles = roles.map((r) => r.toLowerCase());
  return normalizedRoles.some((role) =>
    allowedRoles.map((r) => r.toLowerCase()).includes(role)
  );
}

/**
 * Get list of all personas user can access.
 * Used for filtering navigation items in the sidebar.
 *
 * @param roles - User's roles from current tenant membership
 * @returns Array of accessible persona routes
 */
export function getAccessiblePersonas(roles: string[]): PersonaRoute[] {
  const accessible: PersonaRoute[] = [];
  if (canAccessPersona(roles, 'self-care')) accessible.push('self-care');
  if (canAccessPersona(roles, 'assist')) accessible.push('assist');
  // `admin` is intentionally excluded — the /admin section is hidden (EL-502).
  return accessible;
}

/**
 * Extract persona from pathname.
 *
 * @param pathname - Current URL pathname
 * @returns The persona route if path matches, or null
 */
export function getPersonaFromPath(pathname: string): PersonaRoute | null {
  if (pathname.startsWith('/self-care')) return 'self-care';
  if (pathname.startsWith('/insights')) return 'assist';
  if (pathname.startsWith('/assist')) return 'assist'; // Legacy path support
  if (pathname.startsWith('/admin')) return 'admin';
  return null;
}

/**
 * Check if a tenant membership is marked as internal-only.
 * Internal-only tenants cannot access /app/* routes and should be redirected to /manage.
 *
 * @param membership - The tenant membership to check (null if no tenant selected)
 * @returns true if the tenant is internal-only
 */
export function isInternalOnlyTenant(membership: TenantMembership | null): boolean {
  return membership?.internalOnly === true;
}

/**
 * Check if user has at least one tenant that is not internal-only.
 * Used to determine if "Switch Workspace" option should be shown.
 *
 * @param memberships - User's list of tenant memberships
 * @returns true if at least one non-internal tenant exists
 */
export function hasNonInternalTenant(memberships: TenantMembership[]): boolean {
  return memberships.some(m => !m.internalOnly);
}

/**
 * Get list of tenants that are not internal-only.
 * Used to show available tenants in the InternalOnlyForbiddenPage.
 *
 * @param memberships - User's list of tenant memberships
 * @returns Array of non-internal tenant memberships
 */
export function getNonInternalTenants(memberships: TenantMembership[]): TenantMembership[] {
  return memberships.filter(m => !m.internalOnly);
}
